import React, { useState, useEffect, useRef } from 'react';
import './TodoDatePicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

/**
 * TodoDatePicker - Date picker for Todo due dates with year functionality
 * Based on AccountDatePicker but includes year selection
 */
const TodoDatePicker = ({ 
  value, 
  onChange, 
  name, 
  id,
  placeholder = 'Select due date', 
  className = '', 
  required = false,
  label,
  disabled = false,
}) => {
  // State for the calendar visibility
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State for current month being viewed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // State for the selected date (from the value prop)
  const [selectedDate, setSelectedDate] = useState(null);
  
  // State for year dropdown
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  
  // Ref for the calendar popup to handle outside clicks
  const calendarRef = useRef(null);
  
  // Ref for the input container to position the calendar
  const inputContainerRef = useRef(null);
  
  // Effect to parse the initial value if any
  useEffect(() => {
    if (value) {
      try {
        const parsedDate = new Date(value);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
          setCurrentMonth(parsedDate);
        }
      } catch (error) {
        console.error("Error parsing date:", error);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);
  
  // Effect to handle clicking outside the calendar to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target) &&
          inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
        setShowYearDropdown(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCalendarOpen]);
  
  const toggleCalendar = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
      setShowYearDropdown(false);
    }
  };
  
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    
    // Format date as YYYY-MM-DD for the backend
    const formattedDate = formatDateForBackend(date);
    
    // Call onChange with the formatted date
    if (onChange) {
      onChange({ target: { name: name, value: formattedDate } });
    }
    
    setIsCalendarOpen(false);
    setShowYearDropdown(false);
  };
  
  const formatDateForBackend = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + direction);
      return newMonth;
    });
    setShowYearDropdown(false);
  };
  
  const selectYear = (year) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setFullYear(year);
      return newMonth;
    });
    setShowYearDropdown(false);
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };
  
  const isDateSelected = (date) => {
    if (!selectedDate || !date) return false;
    return selectedDate.getDate() === date.getDate() &&
           selectedDate.getMonth() === date.getMonth() &&
           selectedDate.getFullYear() === date.getFullYear();
  };
  
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  // Generate year range (current year - 1 to current year + 10)
  const generateYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 1; year <= currentYear + 10; year++) {
      years.push(year);
    }
    return years;
  };

  return (
    <div className={`todo-date-picker-container ${className} ${isCalendarOpen ? 'calendar-open' : ''}`}>
      {label && (
        <label htmlFor={id || name} className="todo-date-picker-label">
          {label} {required && <span className="required-field">*</span>}
        </label>
      )}
      
      <div className="todo-date-picker-input-container" ref={inputContainerRef}>
        <input
          type="text"
          id={id || name}
          name={name}
          value={selectedDate ? formatDateForDisplay(selectedDate) : ''}
          placeholder={placeholder}
          className={`todo-date-picker-input ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          required={required}
          readOnly={true}
          onClick={toggleCalendar}
          aria-label="Due date input"
        />
        
        <button 
          type="button" 
          onClick={toggleCalendar} 
          className={`todo-date-picker-button ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          aria-label="Open date calendar"
        >
          <FontAwesomeIcon icon={faCalendar} />
        </button>
      </div>
      
      {isCalendarOpen && (
        <div className="todo-date-picker-calendar" ref={calendarRef}>
          <div className="todo-calendar-header">
            <button 
              type="button"
              onClick={() => navigateMonth(-1)} 
              className="todo-calendar-nav-button"
              aria-label="Previous month"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="todo-calendar-nav-icon" />
            </button>
            
            <div className="todo-calendar-header-title">
              <span className="todo-calendar-month-name">
                {currentMonth.toLocaleDateString('en-US', { month: 'long' })}
              </span>
              <div className="todo-calendar-year-selector">
                <button
                  type="button"
                  className="todo-calendar-year-button"
                  onClick={() => setShowYearDropdown(!showYearDropdown)}
                >
                  {currentMonth.getFullYear()}
                </button>
                {showYearDropdown && (
                  <div className="todo-calendar-year-dropdown">
                    {generateYearRange().map(year => (
                      <button
                        key={year}
                        type="button"
                        className={`todo-calendar-year-option ${year === currentMonth.getFullYear() ? 'selected' : ''}`}
                        onClick={() => selectYear(year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <button 
              type="button"
              onClick={() => navigateMonth(1)} 
              className="todo-calendar-nav-button"
              aria-label="Next month"
            >
              <FontAwesomeIcon icon={faChevronRight} className="todo-calendar-nav-icon" />
            </button>
          </div>
          
          <div className="todo-calendar-days-header">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
              <div key={index} className="todo-calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="todo-calendar-days-grid">
            {getDaysInMonth(currentMonth).map((date, index) => (
              <div
                key={index}
                className={`todo-calendar-day-cell ${
                  !date ? 'empty-cell' : ''
                } ${
                  isDateSelected(date) ? 'selected' : ''
                } ${
                  isToday(date) ? 'today' : ''
                }`}
                onClick={() => date && handleDateSelect(date)}
              >
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoDatePicker;
